import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import InlineOptIn from '../../components/InlineOptIn';

const BlogPost_MuscleTwitchingInSleep = () => {
  // JSON-LD Schema for SEO (FAQ)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [{
      "@type": "Question",
      "name": "Why do I twitch more at night?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Nighttime twitching often feels worse due to 'sensory gating.' During the day, external stimuli mask the sensation. At night, in a quiet room, the brain focuses on internal signals. Additionally, cortisol levels dropping while the nervous system remains hyper-aroused (tired but wired) can trigger fasciculations."
      }
    }, {
      "@type": "Question",
      "name": "Are nighttime twitches dangerous?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "In the vast majority of cases, no. Sleep starts (hypnic jerks) and benign fasciculations are normal physiological phenomena. They are rarely signs of disease unless accompanied by clinical weakness, breathing difficulties, or seizures."
      }
    }, {
      "@type": "Question",
      "name": "Can you feel fasciculations while asleep?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is possible to be woken up by them, or for a partner to notice them, but true fasciculations often continue whether you are awake or asleep. If you only twitch when you are drifting off, it is likely a hypnic jerk."
      }
    }, {
      "@type": "Question",
      "name": "Why does anxiety make twitching worse?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Anxiety floods the body with adrenaline and cortisol, which lowers the excitation threshold of motor neurons. This makes nerves 'trigger happy,' leading to spontaneous firing even when you are trying to rest."
      }
    }, {
      "@type": "Question",
      "name": "Can magnesium stop twitching?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, for many people. Magnesium acts as a natural calcium blocker, helping nerve endings relax. Magnesium Glycinate is the preferred form for sleep-related twitching."
      }
    }]
  };

  return (
    <>
      <Helmet>
        <title>Muscle Twitching in Sleep: Causes, Anxiety, and When to Worry</title>
        <meta name="description" content="Waking up to muscle twitching? A comprehensive medical guide on sleep fasciculations, hypnic jerks, anxiety, and why it's usually benign." />
        <link rel="canonical" href="https://benignfasciculationsyndrome.org/blog/muscle-twitching-in-sleep" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Muscle Twitching in Sleep: Causes, Anxiety, and When to Worry" />
        <meta property="og:description" content="A 3,000+ word pillar guide on why your body twitches at night, the link to anxiety and BFS, and how to finally get some rest." />
        <meta property="og:url" content="https://benignfasciculationsyndrome.org/blog/muscle-twitching-in-sleep" />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* ========== HERO BANNER ========== */}
      <div
        style={{
          backgroundColor: '#003f5c',
          color: '#fff',
          padding: '4rem 0',
          textAlign: 'center',
        }}
      >
        <Container>
          <p className="text-white-50">Pillar Guide | November 15, 2025</p>
          <h1 style={{ fontWeight: '700', fontSize: '2.5rem', maxWidth: '900px', margin: '0 auto' }}>
            Muscle Twitching in Sleep: Causes, Anxiety, and When to Worry
          </h1>
          <p className="lead mt-3" style={{ fontSize: '1.25rem', fontWeight: '300', maxWidth: '700px', margin: '0 auto', opacity: '0.9' }}>
            If your body seems to come alive with pops, jerks, and flutters the moment you try to rest, you are not alone. Here is the definitive medical guide to quieting the nighttime noise.
          </p>
        </Container>
      </div>

      {/* ========== ARTICLE CONTENT ========== */}
      <Container style={{ maxWidth: '800px', padding: '4rem 1rem' }}>
        <Row>
          <Col>
            <p className="lead">
              The house is quiet. The lights are out. You are exhausted after a long day, and your body is desperate for rest. But just as you begin to drift off, it starts. A thump in your calf. A flutter in your eyelid. A sudden jerk of your leg that wakes you up with a gasp.
            </p>
            <p>
              For millions of people, <strong>muscle twitching in sleep</strong> is not just a nuisance; it is a source of profound anxiety. It turns the sanctuary of the bedroom into a battleground of body monitoring. You lie there, waiting for the next pop, wondering, <em>"Why is my body doing this? Is this normal? Is this the start of something serious?"</em>
            </p>
            <p>
              If this sounds familiar, take a deep breath. You have found the right guide.
            </p>
            <p>
              This article is designed to be your anchor. We are going to move past the scary headlines and dive into the actual neurology of sleep twitching. We will distinguish between harmless "hypnic jerks" and Benign Fasciculation Syndrome (BFS), explore why anxiety is the fuel for the fire, and provide you with evidence-based strategies to calm your nervous system and reclaim your sleep.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">Why Do Muscles Twitch During Sleep?</h2>
            <p>
              To understand why you are twitching, we first need to understand how your nervous system handles the transition from "awake" to "asleep."
            </p>
            <p>
              Your nervous system is composed of two main parts: the Central Nervous System (brain and spinal cord) and the Peripheral Nervous System (the nerves that branch out to your muscles). During the day, your brain sends voluntary signals to your muscles to move. However, when you sleep, your brain is supposed to engage a mechanism called "sleep atonia"—essentially a temporary paralysis that prevents you from acting out your dreams.
            </p>
            <p>
              <strong>Twitching in sleep</strong> occurs when there is a "glitch" in this switch-over process, or when the peripheral nerves remain hyper-excited despite the brain trying to shut down.
            </p>
            <p>
              Think of your body like a car engine. Ideally, when you park the car (sleep), the engine turns off completely. But if the engine has been running hot all day (anxiety/stress), or if the fuel mix is off (electrolytes), the engine might sputter and backfire a few times before it finally cools down. Those "backfires" are the fasciculations and jerks you feel.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">Hypnic Jerks vs Muscle Fasciculations: What’s the Difference?</h2>
            <p>
              Not all night movements are the same. It is crucial to distinguish between the two most common types of sleep movement, as they have different causes.
            </p>

            <h4 className="mt-3 fw-semibold">1. Hypnic Jerks (Sleep Starts)</h4>
            <p>
              A hypnic jerk is that sudden, violent "jolt" that happens right as you are falling asleep. You might feel a sensation of falling, followed by a whole-body contraction that wakes you up.
            </p>
            <ul>
              <li><strong>Definition:</strong> An involuntary myoclonic twitch often accompanied by a hallucination of falling.</li>
              <li><strong>Why they occur:</strong> As your muscles relax deeply during the transition to sleep, your brain sometimes misinterprets this relaxation as "falling down." In a primitive protective reflex, the brain fires a signal to the muscles to "catch" you.</li>
              <li><strong>Are they normal?</strong> Yes. Up to 70% of humans experience them. They are exacerbated by caffeine, stress, and sleep deprivation.</li>
            </ul>

            <h4 className="mt-3 fw-semibold">2. Muscle Fasciculations (The "Popcorn" Effect)</h4>
            <p>
              Unlike the single violent jolt of a hypnic jerk, fasciculations are small, rhythmic, or random twitching of specific muscle fibers. This is the "pop... pop... pop" sensation you might feel in your calves or arches.
            </p>
            <ul>
              <li><strong>Definition:</strong> Spontaneous discharge of a single motor unit.</li>
              <li><strong>Why they occur:</strong> These are usually signs of peripheral nerve hyperexcitability (BFS). The nerve ending is irritated or sensitive and fires without a command from the brain.</li>
              <li><strong>The Anxiety Connection:</strong> While hypnic jerks are a brain reflex, fasciculations are often a result of chemical imbalances (cortisol/adrenaline) at the nerve ending itself.</li>
            </ul>

            <h2 className="mt-5 mb-3 fw-bold">The Most Common Causes of Twitching in Sleep</h2>
            <p>
              If you are experiencing <Link to="/blog/calf-twitching-at-night">calf twitching at night</Link> or body-wide popping, it is rarely idiopathic (without cause). There is almost always a trigger. Here are the most common ones, ranked by frequency.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Stress & Anxiety</h3>
            <p>
              Anxiety is not just "in your head"; it is a systemic physiological state. When you are chronically stressed, your body maintains high levels of cortisol and adrenaline. These chemicals are designed to keep your muscles primed for "fight or flight."
            </p>
            <p>
              <strong>The Mechanism:</strong> Adrenaline lowers the excitation threshold of your motor neurons. Normally, it takes a strong signal from the brain to make a muscle move. Under the influence of stress hormones, it takes only a tiny, stray electrical impulse to trigger a twitch. When you lie down to sleep, you might be mentally tired, but your nerves are chemically wired.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Magnesium Deficiency</h3>
            <p>
              Magnesium acts as the "brake pedal" for your nervous system. It blocks calcium (the "gas pedal") from entering nerve endings and causing contractions. If you are deficient in magnesium—which is common in modern diets—your nerves lose their natural braking system. This leads to <Link to="/blog/calf-twitching-magnesium">magnesium for muscle twitching</Link> being a primary first-line treatment.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Electrolyte Imbalance</h3>
            <p>
              Nerves communicate via electricity, generated by the exchange of ions (Sodium, Potassium, Calcium). If these ratios are thrown off—perhaps by a salty dinner, alcohol, or sweating—the electrical signals can become erratic, leading to misfiring during rest.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Dehydration</h3>
            <p>
              Water is the medium in which your nerves operate. Even mild dehydration thickens the blood and alters the concentration of electrolytes. The space between the nerve and the muscle (the synaptic cleft) requires precise hydration to function correctly. When dry, signals can "arc" or misfire, causing twitching.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Sleep Deprivation</h3>
            <p>
              This is a vicious cycle. Twitching keeps you awake, and lack of sleep makes you twitch. Sleep deprivation increases the excitability of the cortex. It removes the "dampening" filters that the brain normally uses to suppress stray signals.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Overexertion / Exercise</h3>
            <p>
              Did you go for a run or lift weights today? <Link to="/blog/calf-twitching-after-exercise">Muscle twitching after exercise</Link> is extremely common. Exercise causes micro-trauma to the muscle fibers and creates lactic acid. As the muscle attempts to repair itself during sleep, it often releases electrical discharges, resulting in twitching.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Caffeine and Stimulants</h3>
            <p>
              Caffeine has a half-life of 5-6 hours. If you have a coffee at 4 PM, half of that caffeine is still active in your system at 10 PM. Caffeine blocks adenosine (the chemical that makes you sleepy) and increases dopamine signaling, keeping motor neurons in a heightened state of alert.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Post-Viral Nerve Sensitivity</h3>
            <p>
              Many people report the onset of BFS or sleep twitching after a viral illness (like COVID-19 or the flu). This is due to a temporary inflammatory response where the immune system slightly irritates the peripheral nerves. This usually resolves with time.
            </p>

            <h3 className="mt-4 mb-3 fw-semibold">Medications</h3>
            <p>
              Certain medications, specifically SSRIs (antidepressants), corticosteroids, and excessive use of albuterol (asthma inhalers), list fasciculations as a known side effect.
            </p>

            <img 
              src="https://images.pexels.com/photos/3771069/pexels-photo-3771069.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
              alt="Person lying awake in bed looking at ceiling"
              className="img-fluid rounded my-4"
            />

            <h2 className="mt-5 mb-3 fw-bold">Can Benign Fasciculation Syndrome (BFS) Cause Twitching in Sleep?</h2>
            <p>
              Absolutely. In fact, twitching while at rest is a hallmark of BFS.
            </p>
            <p>
              In BFS, the peripheral nerves are chronically hyperexcitable. While you are moving during the day, voluntary muscle contractions mask these small twitches. You simply don't feel them because you are walking, talking, or working.
            </p>
            <p>
              When you stop moving, that "masking" effect disappears. The background noise of the BFS becomes the only signal, and suddenly it feels like your legs are popping uncontrollably.
            </p>
            <p>
              <strong>The Reassurance:</strong> The key differentiator is weakness. If you have <Link to="/blog/muscle-twitching-without-weakness">muscle twitching without weakness</Link>, regardless of how violent or frequent it is at night, it points strongly toward BFS and away from degenerative conditions.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">Why Twitching Feels Worse at Night (The “Silent Environment” Effect)</h2>
            <p>
              Patients frequently tell their neurologists, "I'm fine all day, but the second I lie down, it's a party in my legs."
            </p>
            <p>
              This phenomenon is due to <strong>Sensory Gating</strong>.
            </p>
            <ul>
              <li><strong>Daytime:</strong> Your brain is processing millions of bits of data: visual sights, conversations, the feeling of clothes, traffic noise. Your brain actively filters out "low priority" signals like small muscle twitches.</li>
              <li><strong>Nighttime:</strong> You turn off the lights. It is quiet. You are lying still. The external data stream stops. Suddenly, the brain turns its attention inward. That tiny twitch in your arch, which has been happening all day, now feels like a massive thump because it is the <em>only</em> thing happening.</li>
            </ul>
            <p>
              This is amplified by <strong>Hypervigilance</strong>. If you are scared of the twitching, your brain tags the sensation as a "threat." It then amplifies the signal, making you feel even the tiniest vibration.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">Twitching During Sleep vs Twitching When Falling Asleep</h2>
            <p>
              It is helpful to differentiate between <em>hypnagogic</em> twitching (falling asleep) and actual sleep movements.
            </p>
            <p>
              <strong>Hypnagogic/Hypnopompic Twitching:</strong> This happens in the twilight zone between wake and sleep. It is often driven by the brain's "mode switching" errors. This is where hypnic jerks live.
            </p>
            <p>
              <strong>True Sleep Fasciculations:</strong> If your partner notices your calves twitching while you are deep asleep, this is purely physiological. It confirms that the twitching is not "all in your head," but it also confirms it is likely metabolic or related to BFS, as the conscious brain is offline.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">When Muscle Twitching at Night Is NOT Serious</h2>
            <p>
              It is easy to panic in the middle of the night. Here is a checklist to help you rationalize the symptoms and lower your anxiety.
            </p>
            <ul>
              <li><strong>No Clinical Weakness:</strong> You can still stand on your toes, turn door knobs, and lift your head.</li>
              <li><strong>Episodic Nature:</strong> The twitching comes and goes. It might be bad for a week, then gone for a week. Degenerative diseases do not take breaks.</li>
              <li><strong>Widespread Location:</strong> One night it's your left calf, the next it's your right eyelid, then your tricep. This "wandering" twitching is classic <Link to="/blog/body-twitching-all-over">body twitching all over</Link> associated with BFS.</li>
              <li><strong>Trigger-Based:</strong> You notice it's worse after a stressful day, a heavy workout, or a night of drinking.</li>
            </ul>

            <h2 className="mt-5 mb-3 fw-bold">When Nighttime Twitching Could Be a Red Flag</h2>
            <p>
              While rare, there are instances where nighttime movements warrant a doctor's visit.
            </p>
            <ul>
              <li><strong>Progressive Weakness:</strong> You are tripping over your own feet or dropping your coffee cup in the morning.</li>
              <li><strong>Sleep Apnea Signs:</strong> If the twitching is accompanied by gasping, choking, or stopping breathing.</li>
              <li><strong>Rhythmic Kicking (PLMD):</strong> If your legs kick rhythmically every 20-40 seconds all night, this is likely Periodic Limb Movement Disorder, which is treatable but distinct from BFS.</li>
              <li><strong>Atrophy:</strong> Visible loss of muscle mass in the specific area that is twitching.</li>
            </ul>
            <p>
              Understanding the <Link to="/blog/benign-fasciculation-syndrome-vs-als">BFS vs ALS differences</Link> can be helpful, but remember: ALS is a disease of failure, not a disease of feeling. If you feel twitching but your muscles work fine, the odds are in your favor.
            </p>

            <InlineOptIn source="muscle-twitching-in-sleep" offer="protocol" />
            <h2 className="mt-5 mb-3 fw-bold">How to Reduce Nighttime Twitching Naturally</h2>
            <p>
              You cannot always force a nerve to stop firing, but you can create an environment where it is less likely to do so.
            </p>

            <h4 className="mt-3 fw-semibold">1. The Hydration Strategy</h4>
            <p>
              Do not chug a liter of water right before bed (waking up to pee ruins sleep cycles). Instead, front-load your hydration. Drink consistently throughout the morning and afternoon. Stop heavy fluids 2 hours before bed.
            </p>

            <h4 className="mt-3 fw-semibold">2. Magnesium Glycinate</h4>
            <p>
              This is the gold standard supplement for BFS. Take 200-400mg of Magnesium Glycinate 1 hour before bed. It helps relax muscles and lower body temperature.
            </p>

            <h4 className="mt-3 fw-semibold">3. Electrolytes Before Bed</h4>
            <p>
              A small pinch of sea salt in a sip of water, or a low-sugar electrolyte drink, can help stabilize the ion balance in your nerves through the night.
            </p>

            <h4 className="mt-3 fw-semibold">4. Pre-Sleep Stretching</h4>
            <p>
              Tight muscles are twitchy muscles. Spend 5 minutes gently stretching your calves and hamstrings. This lengthens the muscle fibers and reduces the tension on the nerve endings.
            </p>

            <h4 className="mt-3 fw-semibold">5. Blue Light Reduction</h4>
            <p>
              Blue light suppresses melatonin. Low melatonin means lighter sleep, and lighter sleep means more hypnic jerks. Put your phone away 1 hour before bed.
            </p>

            <h2 className="mt-5 mb-3 fw-bold">How Long Does It Take for Sleep-Related Twitching to Improve?</h2>
            <p>
              If you start these protocols tonight, when will the twitching stop?
            </p>
            <ul>
              <li><strong>Hydration/Electrolytes:</strong> 24-48 hours.</li>
              <li><strong>Magnesium Repletion:</strong> 2-4 weeks of consistent supplementation.</li>
              <li><strong>Stress/Anxiety Reduction:</strong> 4-8 weeks. The nervous system is slow to heal. It takes time to downregulate from a state of hyper-arousal.</li>
              <li><strong>BFS Stabilization:</strong> Variable. For some, it is a chronic condition that waxes and wanes. For others, <Link to="/blog/persistent-calf-twitching">persistent calf twitching</Link> resolves once the anxiety about the twitching is gone.</li>
            </ul>

            <h2 className="mt-5 mb-3 fw-bold">Frequently Asked Questions</h2>
            
            <div className="accordion" id="sleepTwitchFAQ">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingOne">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                    <strong>Why do I twitch more at night?</strong>
                  </button>
                </h2>
                <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#sleepTwitchFAQ">
                  <div className="accordion-body">
                    Nighttime twitching is amplified by the lack of external sensory distractions (Sensory Gating) and the "tired but wired" state where cortisol drops but adrenaline remains high.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingTwo">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                    <strong>Are nighttime twitches dangerous?</strong>
                  </button>
                </h2>
                <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#sleepTwitchFAQ">
                  <div className="accordion-body">
                    Rarely. Unless accompanied by clinical weakness, atrophy, or seizures, they are usually benign physiological events caused by stress, fatigue, or BFS.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingThree">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                    <strong>Can you feel fasciculations while asleep?</strong>
                  </button>
                </h2>
                <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#sleepTwitchFAQ">
                  <div className="accordion-body">
                    It is possible for a strong fasciculation to wake you up, but typically you feel them in the transition to sleep (hypnagogic state). If they happen during deep sleep, you usually won't feel them, though a partner might see them.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingFour">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="false" aria-controls="collapseFour">
                    <strong>Why does anxiety make twitching worse?</strong>
                  </button>
                </h2>
                <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#sleepTwitchFAQ">
                  <div className="accordion-body">
                    Anxiety sensitizes the nervous system. It keeps motor neurons in a state of partial depolarization, meaning it takes very little electrical input to trigger a twitch. This creates a feedback loop of fear and twitching.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingFive">
                  <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFive" aria-expanded="false" aria-controls="collapseFive">
                    <strong>Can magnesium stop twitching?</strong>
                  </button>
                </h2>
                <div id="collapseFive" className="accordion-collapse collapse" aria-labelledby="headingFive" data-bs-parent="#sleepTwitchFAQ">
                  <div className="accordion-body">
                    Yes. Magnesium helps regulate nerve function and muscle relaxation. Deficiencies are a common trigger for twitching. Supplementing with Magnesium Glycinate is often effective.
                  </div>
                </div>
              </div>
            </div>

            <h2 className="mt-5 mb-3 fw-bold">Supplements That Support the Nervous System at Night</h2>
            <p>
              While we do not endorse specific brands, these compounds are supported by research for calming nerve activity:
            </p>
            <ul>
              <li><strong>Magnesium Glycinate:</strong> Highly absorbable, gentle on the stomach, and promotes sleep.</li>
              <li><strong>L-Theanine:</strong> An amino acid found in green tea that increases alpha brain waves (relaxation) without sedation.</li>
              <li><strong>Electrolytes (Potassium/Sodium):</strong> Essential for proper nerve signaling.</li>
              <li><strong>Vitamin D:</strong> Critical for overall neurological health.</li>
              <li><strong>Omega-3 Fatty Acids:</strong> Supports the myelin sheath of the nerves.</li>
            </ul>

            <hr className="my-5" />

            <div className="alert alert-info mt-5">
              <p className="mb-0">
                <em><strong>Disclaimer:</strong> This article is for educational purposes only and does not constitute medical advice. While muscle twitching in sleep is usually benign, persistent symptoms should be evaluated by a healthcare professional.</em>
              </p>
            </div>

            {/* ========== SCIENTIFIC REFERENCES ========== */}
            <h2 className="mt-5 mb-3 fw-bold">Scientific References</h2>
            <p className="small text-muted">
              This article is grounded in peer-reviewed neurological and sleep research.
            </p>
            <ol className="small text-muted">
              <li className="mb-2">
                Chiaro G, et al. Hypnic jerks: clinical and neurophysiological characteristics. <em>Sleep Medicine</em>. 2016.<br />
                <a href="https://pubmed.ncbi.nlm.nih.gov/27448445/" target="_blank" rel="noopener noreferrer">
                  View on PubMed
                </a>
              </li>
              <li className="mb-2">
                American Academy of Sleep Medicine. Sleep Starts (Hypnic Jerks).<br />
                <a href="https://sleepeducation.org/sleep-disorders/sleep-starts/" target="_blank" rel="noopener noreferrer">
                  View Source
                </a>
              </li>
              <li className="mb-2">
                Turner MR, Talbot K. Mimics and chameleons in motor neurone disease. <em>Practical Neurology</em>. 2013.<br />
                <a href="https://pn.bmj.com/content/13/3/153" target="_blank" rel="noopener noreferrer">
                  Read Full Text at BMJ
                </a>
              </li>
              <li className="mb-2">
                National Institute of Neurological Disorders and Stroke (NINDS). Myoclonus Fact Sheet.<br />
                <a href="https://www.ninds.nih.gov/health-information/disorders/myoclonus" target="_blank" rel="noopener noreferrer">
                  Visit NINDS
                </a>
              </li>
            </ol>

            <div className="text-center mt-5">
              <Link to="/blog" className="btn btn-primary px-4" style={{backgroundColor: '#003f5c', borderColor: '#003f5c', borderRadius: '9999px', fontWeight: '600'}}>
                Back to Blog
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default BlogPost_MuscleTwitchingInSleep;